import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Box, Card, CardContent, Tab, Tabs } from "@mui/material";
import HubIcon from "@mui/icons-material/Hub";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import StorageIcon from "@mui/icons-material/Storage";
import HistoryIcon from "@mui/icons-material/History";
import RuleIcon from "@mui/icons-material/Rule";
import PublicIcon from "@mui/icons-material/Public";

import { Sidebar } from "../../containers/Sidebar/Sidebar";
import { CustomTypography } from "../../components/CustomTypography/CustomTypography";
import useStyles from "./styles";
import { usePoiStats } from "../../api/poi";
import { SourcesTab } from "./SourcesTab";
import { ImportTab } from "./ImportTab";
import { RunsTab } from "./RunsTab";
import { ReviewTab } from "./ReviewTab";
import { CoverageTab } from "./CoverageTab";

/**
 * Centres & Stations — the operator surface for the multi-source ingestion
 * pipeline (garages, contrôle technique, stations-service, bornes de recharge).
 *
 * Replaces the single "Import stations gouvernementales" button that lived
 * under Services. That button could only do two French feeds and gave no
 * feedback beyond a toast; this screen exposes the whole registry, the run
 * history with rejection breakdowns, and the manual match-review queue.
 */
type Props = {};

export const Poi: React.FC<Props> = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);

  const { stats } = usePoiStats();

  const kpis = [
    {
      label: t("poi.kpiEntities", "Établissements"),
      value: stats?.entities ?? 0,
      color: "primary.main",
    },
    {
      label: t("poi.kpiLinked", "Publiés (signs)"),
      value: stats?.signs_linked ?? 0,
      color: "success.main",
    },
    {
      label: t("poi.kpiStaged", "Fiches sources"),
      value: stats?.staged_records ?? 0,
      color: "text.primary",
    },
    {
      label: t("poi.kpiReview", "À vérifier"),
      value: stats?.review_queue ?? 0,
      color: (stats?.review_queue ?? 0) > 0 ? "warning.main" : "text.primary",
    },
    {
      label: t("poi.kpiRejected", "Rejetés"),
      value: stats?.rejected ?? 0,
      color: (stats?.rejected ?? 0) > 0 ? "error.main" : "text.primary",
    },
    {
      label: t("poi.kpiLocked", "Champs verrouillés"),
      value: stats?.locked_fields ?? 0,
      color: "text.primary",
    },
  ];

  return (
    <Sidebar
      styles={{
        sidebarRightRoot: classes.poiRoot,
        sidebarRoot: classes.contentMain,
      }}
    >
      <div className={classes.containerHeader}>
        <div className={classes.divIconArrow}>
          <HubIcon sx={{ fontSize: 32, color: "primary.main" }} />
          <CustomTypography
            variant="h2"
            text={t("poi.title", "Centres & Stations")}
            isUppercase
          />
        </div>
      </div>

      {/* ── KPI strip ──────────────────────────────────────────────────── */}
      <div className={classes.statsRow}>
        {kpis.map((kpi) => (
          <Card key={kpi.label} className={classes.statCard}>
            <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
              <Box className={classes.statValue} sx={{ color: kpi.color }}>
                {kpi.value.toLocaleString("fr-FR")}
              </Box>
              <Box className={classes.statLabel}>{kpi.label}</Box>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* ── Tabs ───────────────────────────────────────────────────────── */}
      <Card className={classes.card}>
        <CardContent className={classes.cardContent}>
          <Tabs
            value={tab}
            onChange={(_e, v) => setTab(v)}
            className={classes.tabsBar}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            {/*
              Import first: it is the only tab that works with zero setup — no
              API key, no connector wiring, just drop a file. Sources leads with
              a table of things that mostly need credentials, which is a worse
              first impression on an empty install.
            */}
            <Tab
              icon={<UploadFileIcon />}
              iconPosition="start"
              label={t("poi.tabImport", "Import")}
            />
            <Tab
              icon={<StorageIcon />}
              iconPosition="start"
              label={t("poi.tabSources", "Sources")}
            />
            <Tab
              icon={<HistoryIcon />}
              iconPosition="start"
              label={t("poi.tabRuns", "Historique")}
            />
            <Tab
              icon={
                /* The badge is the whole point of this tab: an operator should
                   see pending decisions without opening it. */
                <Badge
                  color="warning"
                  badgeContent={stats?.review_queue ?? 0}
                  max={999}
                  invisible={!stats?.review_queue}
                >
                  <RuleIcon />
                </Badge>
              }
              iconPosition="start"
              label={t("poi.tabReview", "Vérification")}
            />
            <Tab
              icon={<PublicIcon />}
              iconPosition="start"
              label={t("poi.tabCoverage", "Couverture")}
            />
          </Tabs>

          {tab === 0 && <ImportTab />}
          {tab === 1 && <SourcesTab />}
          {tab === 2 && <RunsTab />}
          {tab === 3 && <ReviewTab />}
          {tab === 4 && <CoverageTab />}
        </CardContent>
      </Card>
    </Sidebar>
  );
};
